// World tab — map size, pacing and intensity tuning. Split out of
// CustomizeTabs.tsx.

import { type AodMapV1, DEFAULT_YEAR_MINUTES, validateAodMap } from '@aod/protocol';
import type { ModuleId, Tuning } from '@aod/protocol';
import { FileUp, Link2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { type MessageKey, useT } from '../../i18n/index.js';
import type { TabProps } from '../CustomizeTabs.js';
import { SegmentedRow, StepperRow, SwitchRow } from '../controls.js';
import { moduleLabel } from '../presentation.js';
import {
  type SetupDraft,
  setAuthoredMap,
  setClimate,
  setSea,
  setThirst,
  setTuning,
  setYearMinutes,
} from '../setupModel.js';

// ── World ──────────────────────────────────────────────────────────────

export function WorldTab({ draft, onChange }: TabProps) {
  const t = useT();
  const fileInput = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // The authored map is carried as JSON in the draft; re-validate on render
  // so the author sees problems before Start.
  const authored = draft.authoredMapJson;
  const authoredDoc = authored ? (JSON.parse(authored) as AodMapV1) : null;
  const authoredProblems = authored
    ? (() => {
        try {
          return validateAodMap(authoredDoc);
        } catch {
          return [t('setup.authored.invalid')];
        }
      })()
    : [];
  const importMap = (file: File) => {
    void file.text().then((text) => {
      try {
        JSON.parse(text);
      } catch {
        setImportError(t('setup.authored.invalid'));
        return;
      }
      setImportError(null);
      onChange(setAuthoredMap(draft, text));
    });
  };
  const tuning = draft.ruleset.tuning;
  const mods = new Set(draft.ruleset.modules);
  const set = <K extends keyof Tuning>(key: K, value: Tuning[K]) =>
    onChange(setTuning(draft, key, value));
  const options = <
    K extends 'pace' | 'mapSize' | 'startingResources' | 'victoryLength' | 'intensityCeiling',
  >(
    key: K,
    values: readonly Tuning[K][],
  ) => values.map((v) => ({ value: v, label: t(`setup.tuning.${key}.${v}` as MessageKey) }));
  const needs = (m: ModuleId) => (
    <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      <Link2 className="size-3" aria-hidden />
      {t('setup.needs', { list: moduleLabel(t, m) })}
    </span>
  );
  const minutes = (v: number) => (v === 0 ? t('setup.off') : t('setup.minutes', { min: v }));
  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-lg border border-border/60 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileUp className="size-4" aria-hidden />
            <span className="text-sm font-medium">{t('setup.authored.title')}</span>
          </div>
          <button
            type="button"
            className="text-xs underline text-muted-foreground hover:text-foreground"
            onClick={() => fileInput.current?.click()}
          >
            {t('setup.authored.import')}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importMap(f);
              e.target.value = '';
            }}
          />
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">{t('setup.authored.desc')}</p>
        {authored && authoredDoc ? (
          <div className="mt-2 flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5">
            <span className="text-xs" data-testid="authored-map-loaded">
              {authoredDoc.name} · {authoredDoc.tiles.length} tiles
              {authoredProblems.length > 0
                ? ` — ${t('setup.authored.problems', { count: authoredProblems.length })}`
                : ''}
            </span>
            <button
              type="button"
              aria-label={t('setup.authored.clear')}
              onClick={() => {
                setImportError(null);
                onChange(setAuthoredMap(draft, null));
              }}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        ) : null}
        {importError ? <p className="mt-1 text-xs text-destructive">{importError}</p> : null}
      </div>
      <SegmentedRow
        label={t('setup.climate')}
        value={draft.ruleset.climate ?? 'temperate'}
        options={(['temperate', 'arctic', 'tropical', 'desert'] as const).map((v) => ({
          value: v,
          label: t(`setup.climate.${v}` as MessageKey),
        }))}
        onChange={(v) => onChange(setClimate(draft, v))}
      />
      <SegmentedRow
        label={t('setup.sea')}
        description={t('setup.sea.desc')}
        value={draft.ruleset.sea ?? 'rim'}
        options={(['rim', 'landlocked', 'open', 'archipelago'] as const).map((v) => ({
          value: v,
          label: t(`setup.sea.${v}` as MessageKey),
        }))}
        onChange={(v) => onChange(setSea(draft, v))}
      />
      <SegmentedRow
        label={t('setup.thirst')}
        description={t('setup.thirst.desc')}
        value={draft.ruleset.thirst ?? 'auto'}
        options={(['auto', 'on', 'off'] as const).map((v) => ({
          value: v,
          label: t(`setup.thirst.${v}` as MessageKey),
        }))}
        onChange={(v) => onChange(setThirst(draft, v))}
      />
      <SegmentedRow
        label={t('setup.tuning.mapSize')}
        value={tuning.mapSize}
        options={options('mapSize', ['tiny', 'small', 'medium', 'large'])}
        onChange={(v) => set('mapSize', v)}
      />
      <SegmentedRow
        label={t('setup.tuning.pace')}
        value={tuning.pace}
        options={options('pace', ['relaxed', 'normal', 'brisk'])}
        onChange={(v) => set('pace', v)}
      />
      <SegmentedRow
        label={t('setup.tuning.startingResources')}
        value={tuning.startingResources}
        options={options('startingResources', ['low', 'normal', 'high'])}
        onChange={(v) => set('startingResources', v)}
      />
      <SegmentedRow
        label={t('setup.tuning.victoryLength')}
        value={tuning.victoryLength}
        options={options('victoryLength', ['short', 'normal', 'long'])}
        onChange={(v) => set('victoryLength', v)}
      />
      <StepperRow
        label={t('setup.yearLength')}
        description={t('setup.yearLength.desc')}
        value={draft.ruleset.yearMinutes ?? DEFAULT_YEAR_MINUTES}
        min={2}
        max={12}
        step={0.5}
        format={minutes}
        onChange={(v) => onChange(setYearMinutes(draft, v))}
        decreaseLabel={t('setup.decrease', { label: t('setup.yearLength') })}
        increaseLabel={t('setup.increase', { label: t('setup.yearLength') })}
      />
      <StepperRow
        label={t('setup.tuning.peacePeriod')}
        description={t('setup.tuning.peacePeriod.desc')}
        value={tuning.peacePeriodMin}
        min={0}
        max={20}
        step={1}
        format={minutes}
        onChange={(v) => set('peacePeriodMin', v)}
        decreaseLabel={t('setup.decrease', { label: t('setup.tuning.peacePeriod') })}
        increaseLabel={t('setup.increase', { label: t('setup.tuning.peacePeriod') })}
      />
      <SwitchRow
        label={t('setup.tuning.cityCanBeDestroyed')}
        description={t('setup.tuning.cityCanBeDestroyed.desc')}
        checked={tuning.cityCanBeDestroyed}
        onChange={(v) => set('cityCanBeDestroyed', v)}
      />
      <StepperRow
        label={t('setup.tuning.truceMin')}
        description={t('setup.tuning.truceMin.desc')}
        value={tuning.truceMin}
        min={0}
        max={5}
        step={0.5}
        format={minutes}
        onChange={(v) => set('truceMin', v)}
        decreaseLabel={t('setup.decrease', { label: t('setup.tuning.truceMin') })}
        increaseLabel={t('setup.increase', { label: t('setup.tuning.truceMin') })}
        disabled={!mods.has('pacing.truce')}
        note={mods.has('pacing.truce') ? null : needs('pacing.truce')}
      />
      <SegmentedRow
        label={t('setup.tuning.intensityCeiling')}
        value={tuning.intensityCeiling}
        options={options('intensityCeiling', ['low', 'medium', 'high', 'off'])}
        onChange={(v) => set('intensityCeiling', v)}
        disabled={!mods.has('pacing.intensity')}
        note={mods.has('pacing.intensity') ? null : needs('pacing.intensity')}
      />
    </div>
  );
}
